import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { format } from "date-fns";
import {
  Eye,
  Pencil,
  Archive,
  Trash2,
  MoreHorizontal,
  ChevronUp,
  ChevronDown,
  ChevronsUpDown,
  ChevronLeft,
  ChevronRight,
  ExternalLink,
} from "lucide-react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import type { Article } from "@/types/index";

export type SortField = "headline" | "publishedAt" | "category" | "importance";
export type SortOrder = "asc" | "desc";

interface ArticleTableProps {
  articles: Article[];
  isLoading: boolean;
  sortField: SortField;
  sortOrder: SortOrder;
  onSort: (field: SortField) => void;
  selectedIds: Set<string>;
  onSelectionChange: (ids: Set<string>) => void;
  onView: (article: Article) => void;
  onEdit: (article: Article) => void;
  onArchive: (article: Article) => void;
  onDelete: (article: Article) => void;
  page: number;
  pageSize: number;
  totalPages: number;
  totalItems: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (size: number) => void;
}

function getStatusBadge(article: Article): {
  label: string;
  variant: "success" | "warning" | "secondary";
} {
  if (article.isArchived) {
    return { label: "Archived", variant: "secondary" };
  }
  // Check if published (has a publishedAt date in the past)
  const now = new Date();
  const publishedAt = new Date(article.publishedAt);
  if (publishedAt <= now) {
    return { label: "Published", variant: "success" };
  }
  return { label: "Draft", variant: "warning" };
}

function getCategoryColor(category: string): string {
  const colors: Record<string, string> = {
    politics: "bg-blue-500/10 text-blue-600 dark:text-blue-400",
    tech: "bg-purple-500/10 text-purple-600 dark:text-purple-400",
    business: "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400",
    world: "bg-orange-500/10 text-orange-600 dark:text-orange-400",
    sports: "bg-red-500/10 text-red-600 dark:text-red-400",
    general: "bg-gray-500/10 text-gray-600 dark:text-gray-400",
  };
  return colors[category.toLowerCase()] || colors.general;
}

interface SortableHeaderProps {
  field: SortField;
  label: string;
  currentField: SortField;
  currentOrder: SortOrder;
  onSort: (field: SortField) => void;
  className?: string;
}

function SortableHeader({
  field,
  label,
  currentField,
  currentOrder,
  onSort,
  className,
}: SortableHeaderProps) {
  const isActive = currentField === field;

  return (
    <TableHead className={className}>
      <button
        onClick={() => onSort(field)}
        className={cn(
          "flex items-center gap-1 hover:text-foreground transition-colors",
          isActive && "text-foreground"
        )}
      >
        {label}
        {isActive ? (
          currentOrder === "asc" ? (
            <ChevronUp className="h-4 w-4" />
          ) : (
            <ChevronDown className="h-4 w-4" />
          )
        ) : (
          <ChevronsUpDown className="h-3.5 w-3.5 opacity-50" />
        )}
      </button>
    </TableHead>
  );
}

function TableRowSkeleton() {
  return (
    <TableRow>
      <TableCell className="w-[40px]">
        <Skeleton className="h-4 w-4 rounded" />
      </TableCell>
      <TableCell className="w-[80px]">
        <Skeleton className="h-12 w-16 rounded-md" />
      </TableCell>
      <TableCell>
        <div className="space-y-2">
          <Skeleton className="h-4 w-64" />
          <Skeleton className="h-3 w-40" />
        </div>
      </TableCell>
      <TableCell>
        <Skeleton className="h-5 w-20 rounded-full" />
      </TableCell>
      <TableCell>
        <Skeleton className="h-5 w-16 rounded-full" />
      </TableCell>
      <TableCell>
        <Skeleton className="h-4 w-24" />
      </TableCell>
      <TableCell>
        <Skeleton className="h-8 w-8 rounded" />
      </TableCell>
    </TableRow>
  );
}

export function ArticleTable({
  articles,
  isLoading,
  sortField,
  sortOrder,
  onSort,
  selectedIds,
  onSelectionChange,
  onView,
  onEdit,
  onArchive,
  onDelete,
  page,
  pageSize,
  totalPages,
  totalItems,
  onPageChange,
  onPageSizeChange,
}: ArticleTableProps) {
  const [hoveredRow, setHoveredRow] = useState<string | null>(null);

  const startItem = (page - 1) * pageSize + 1;
  const endItem = Math.min(page * pageSize, totalItems);

  const allSelected = articles.length > 0 && articles.every((a) => selectedIds.has(a.id));

  const handleSelectAll = () => {
    if (allSelected) {
      onSelectionChange(new Set());
    } else {
      onSelectionChange(new Set(articles.map((a) => a.id)));
    }
  };

  const handleSelectRow = (id: string) => {
    const newSet = new Set(selectedIds);
    if (newSet.has(id)) {
      newSet.delete(id);
    } else {
      newSet.add(id);
    }
    onSelectionChange(newSet);
  };

  return (
    <div className="space-y-4">
      <div className="rounded-lg border bg-card overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-muted/50 hover:bg-muted/50">
              <TableHead className="w-[40px]">
                <Checkbox
                  checked={allSelected}
                  onCheckedChange={handleSelectAll}
                  aria-label="Select all"
                />
              </TableHead>
              <TableHead className="w-[80px]">Image</TableHead>
              <SortableHeader
                field="headline"
                label="Headline"
                currentField={sortField}
                currentOrder={sortOrder}
                onSort={onSort}
              />
              <SortableHeader
                field="category"
                label="Category"
                currentField={sortField}
                currentOrder={sortOrder}
                onSort={onSort}
                className="w-[120px]"
              />
              <TableHead className="w-[100px]">Status</TableHead>
              <SortableHeader
                field="publishedAt"
                label="Published"
                currentField={sortField}
                currentOrder={sortOrder}
                onSort={onSort}
                className="w-[140px]"
              />
              <TableHead className="w-[60px]" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              Array.from({ length: pageSize }).map((_, i) => (
                <TableRowSkeleton key={i} />
              ))
            ) : articles.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} className="h-32 text-center">
                  <div className="flex flex-col items-center gap-2 text-muted-foreground">
                    <span>No articles found</span>
                    <span className="text-sm">Try adjusting your search or filters</span>
                  </div>
                </TableCell>
              </TableRow>
            ) : (
              <AnimatePresence mode="popLayout">
                {articles.map((article, index) => {
                  const status = getStatusBadge(article);
                  const isHovered = hoveredRow === article.id;
                  const isSelected = selectedIds.has(article.id);

                  return (
                    <motion.tr
                      key={article.id}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, x: -10 }}
                      transition={{ duration: 0.2, delay: index * 0.02 }}
                      className={cn(
                        "border-b transition-colors",
                        isSelected && "bg-primary/5",
                        isHovered && !isSelected && "bg-muted/50"
                      )}
                      onMouseEnter={() => setHoveredRow(article.id)}
                      onMouseLeave={() => setHoveredRow(null)}
                    >
                      <TableCell onClick={(e) => e.stopPropagation()}>
                        <Checkbox
                          checked={isSelected}
                          onCheckedChange={() => handleSelectRow(article.id)}
                          aria-label={`Select ${article.headline}`}
                        />
                      </TableCell>
                      <TableCell>
                        <div className="h-12 w-16 rounded-md overflow-hidden bg-muted">
                          {article.imageUrl ? (
                            <img
                              src={article.imageUrl}
                              alt=""
                              className="h-full w-full object-cover"
                            />
                          ) : (
                            <div className="h-full w-full flex items-center justify-center text-muted-foreground text-xs">
                              No img
                            </div>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <div
                                className="cursor-pointer"
                                onClick={() => onView(article)}
                              >
                                <p className="font-medium line-clamp-1 hover:text-primary transition-colors">
                                  {article.headline}
                                </p>
                                <p className="text-sm text-muted-foreground line-clamp-1 mt-0.5">
                                  {article.summary}
                                </p>
                              </div>
                            </TooltipTrigger>
                            <TooltipContent side="bottom" className="max-w-md">
                              <p className="font-medium">{article.headline}</p>
                              <p className="text-sm text-muted-foreground mt-1">
                                {article.summary}
                              </p>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      </TableCell>
                      <TableCell>
                        <Badge
                          variant="secondary"
                          className={cn(
                            "font-normal capitalize",
                            getCategoryColor(article.category)
                          )}
                        >
                          {article.category}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Badge variant={status.variant}>{status.label}</Badge>
                      </TableCell>
                      <TableCell className="text-muted-foreground text-sm">
                        {format(new Date(article.publishedAt), "MMM d, yyyy")}
                      </TableCell>
                      <TableCell>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button
                              variant="ghost"
                              size="icon"
                              className={cn(
                                "h-8 w-8 transition-opacity",
                                isHovered ? "opacity-100" : "opacity-0"
                              )}
                              onClick={(e) => e.stopPropagation()}
                            >
                              <MoreHorizontal className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="w-48">
                            <DropdownMenuItem
                              onClick={(e) => {
                                e.stopPropagation();
                                onView(article);
                              }}
                            >
                              <Eye className="h-4 w-4 mr-2" />
                              View Article
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              onClick={(e) => {
                                e.stopPropagation();
                                onEdit(article);
                              }}
                            >
                              <Pencil className="h-4 w-4 mr-2" />
                              Edit
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              onClick={(e) => {
                                e.stopPropagation();
                                window.open(`/articles/${article.id}`, "_blank");
                              }}
                            >
                              <ExternalLink className="h-4 w-4 mr-2" />
                              Open in New Tab
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem
                              onClick={(e) => {
                                e.stopPropagation();
                                onArchive(article);
                              }}
                            >
                              <Archive className="h-4 w-4 mr-2" />
                              {article.isArchived ? "Unarchive" : "Archive"}
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              onClick={(e) => {
                                e.stopPropagation();
                                onDelete(article);
                              }}
                              className="text-destructive focus:text-destructive"
                            >
                              <Trash2 className="h-4 w-4 mr-2" />
                              Delete
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </motion.tr>
                  );
                })}
              </AnimatePresence>
            )}
          </TableBody>
        </Table>
      </div>

      {/* Pagination */}
      <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <span>
            Showing {totalItems > 0 ? startItem : 0}-{endItem} of {totalItems} articles
          </span>
          <span className="text-muted-foreground/50">|</span>
          <div className="flex items-center gap-2">
            <span>Per page:</span>
            <Select
              value={pageSize.toString()}
              onValueChange={(v) => onPageSizeChange(parseInt(v, 10))}
            >
              <SelectTrigger className="h-8 w-[70px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="10">10</SelectItem>
                <SelectItem value="25">25</SelectItem>
                <SelectItem value="50">50</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="flex items-center gap-1">
          <Button
            variant="outline"
            size="icon"
            className="h-8 w-8"
            onClick={() => onPageChange(page - 1)}
            disabled={page <= 1}
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>

          {/* Page numbers */}
          <div className="flex items-center gap-1">
            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
              let pageNum: number;
              if (totalPages <= 5) {
                pageNum = i + 1;
              } else if (page <= 3) {
                pageNum = i + 1;
              } else if (page >= totalPages - 2) {
                pageNum = totalPages - 4 + i;
              } else {
                pageNum = page - 2 + i;
              }

              return (
                <Button
                  key={pageNum}
                  variant={page === pageNum ? "default" : "outline"}
                  size="icon"
                  className="h-8 w-8"
                  onClick={() => onPageChange(pageNum)}
                >
                  {pageNum}
                </Button>
              );
            })}
          </div>

          <Button
            variant="outline"
            size="icon"
            className="h-8 w-8"
            onClick={() => onPageChange(page + 1)}
            disabled={page >= totalPages}
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}
